import React, { useEffect, useState } from "react"
import { useParams, useNavigate, useLocation } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { Button, Tag, Image, Dropdown, Flex, Spin } from "antd"
import DSTab from "ui/DSTab"
import { Icon } from "ui/Icon"
import { routes } from "router/routes"
import { getCrewProfile, updateCrewProfile } from "store/crew-profiles"
import { getRoles } from "store/roles"
import UpcomingBookings from "./components/UpcomingBookings"
import Availability from "./components/Availability"
import ScheduledTimeOff from "./components/ScheduledTimeOff"
import AssignedExperiences from "./components/AssignedExperiences"
import LicensesCertifications from "./components/LicensesCertifications"
import Calendar from "./components/Calendar"
import EditCrewMemberModal from "./components/EditCrewMemberModal"
import AssignExperiencesModal from "ui/AssignExperiencesModal"
import DeactivateDeleteModals from "./components/DeactivateDeleteModals"
import { useCrewDeactivateModal } from "./hooks/useCrewDeactivateModal"
import styles from "./index.module.css"
import MoreActionEllipsis from "ui/MoreActionEllipsis"
import { toaster } from "components/common/Toast"

// Transform API data to component format
const transformProfileData = (apiData) => {
  if (!apiData) return null

  return {
    id: apiData.id,
    firstName: apiData.first_name,
    lastName: apiData.last_name,
    name: `${apiData.first_name} ${apiData.last_name}`,
    email: apiData.email,
    phoneNumber: apiData.phone_number,
    phoneCountryCode: apiData.country_code,
    title: apiData.title,
    role: apiData.role?.name,
    roleId: apiData.role?.id,
    description: apiData.description,
    status: apiData.status === "active" ? "Available" : "Deactivated",
    image: apiData.photo_url || apiData.photo,
    // These will come from separate API calls or be part of the profile response
    upcomingBookings: apiData.upcoming_bookings || [],
    availability: apiData.availability || {},
    scheduledTimeOff: apiData.scheduled_time_off || [],
    assignedExperiences: apiData.assigned_experiences || [],
    licenses: apiData.licenses || []
  }
}

export default function CrewMemberDetailPage() {
  const { t } = useTranslation()
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isAssignExperiencesModalOpen, setIsAssignExperiencesModalOpen] = useState(false)

  // Deactivate/Delete modal hook
  const {
    deactivateDeleteModalState,
    openDeactivateModal,
    openDeleteModal,
    closeDeactivateDeleteModal,
    handleConfirmDeactivateDelete,
    deactivateLoading,
    deleteLoading,
    deactivationDetailsLoading,
    deletionDetailsLoading
  } = useCrewDeactivateModal()

  // Redux state
  const { profile } = useSelector((state) => state.crewProfiles)
  const { items: roles } = useSelector((state) => state.roles)

  // Fetch crew profile and roles on mount
  useEffect(() => {
    dispatch(getCrewProfile(id))
    dispatch(getRoles())
  }, [dispatch, id])

  // Transform API data for display
  const crewMember = transformProfileData(profile.data)
  const { image, name, role, status, title, description, upcomingBookings, availability, scheduledTimeOff, assignedExperiences, licenses } = crewMember || {}
  const isLoading = profile.loading

  // Determine active tab from URL
  const activeTab = location.pathname.includes("/calendar") ? "calendar" : "details"

  // Redirect to details if on base crew member URL
  useEffect(() => {
    if (location.pathname === `/pro/home/resources/crew/${id}`) {
      navigate(routes.guideCrewMemberDetailsPath({ id }), { replace: true })
    }
  }, [location.pathname, id, navigate])

  const handleEdit = () => {
    setIsEditModalOpen(true)
  }

  const handleSaveEdit = async (values) => {
    const formData = new FormData()

    // Extract phone number and country code from phone object
    const phoneNumber = values.phoneNumber?.fullNumber || ""
    const countryCode = values.phoneNumber?.countryCode || "US"

    // Map form values to API format
    formData.append("profile[first_name]", values.firstName)
    formData.append("profile[last_name]", values.lastName)
    formData.append("profile[email]", values.email)
    formData.append("profile[phone_number]", phoneNumber)
    formData.append("profile[country_code]", countryCode)
    formData.append("profile[title]", values.title || "")
    formData.append("profile[role_id]", values.roleId)
    formData.append("profile[description]", values.description || "")

    // Map status: Available -> active, others -> deactivated
    const apiStatus = values.status === "Available" ? "active" : "deactivated"
    formData.append("profile[status]", apiStatus)

    // Handle photo upload if it's a File object
    if (values.photoFile) {
      formData.append("profile[photo]", values.photoFile)
    }

    try {
      await dispatch(updateCrewProfile(id, formData))
      toaster.success(t("crewMember.detail.messages.updateSuccess"))
      setIsEditModalOpen(false)
    } catch (error) {
      console.error(t("crewMember.detail.messages.updateError"), error)
    }
  }

  const handleBack = () => {
    navigate(routes.guideResourcesCrewPath())
  }

  const handleTabChange = (key) => {
    if (key === "details") {
      navigate(routes.guideCrewMemberDetailsPath({ id }))
    } else if (key === "calendar") {
      navigate(routes.guideCrewMemberCalendarPath({ id }))
    }
  }

  const tabItems = [
    {
      key: "details",
      label: t("crewMember.detail.tabs.details")
    },
    {
      key: "calendar",
      label: t("crewMember.detail.tabs.calendar")
    }
  ]

  // Show loading state
  if (isLoading && !crewMember) {
    return (
      <Flex vertical gap={16} className={`${styles.container} ${styles.loadingContainer}`} align="center" justify="center">
        <Spin size="large" />
      </Flex>
    )
  }

  return (
    <Flex vertical gap={16} className={styles.container}>
      {/* Back Button */}
      <Flex align="center" className={`mt-m ${styles.pageNameSection}`}>
        <Flex align="center" justify="center" onClick={handleBack} className={styles.backButton}>
          <Icon width={16} height={16} type="feather" name="ChevronLeft" color="currentColor" />
        </Flex>
      </Flex>

      {/* Header Section */}
      <div className={styles.headerCard}>
        <Flex align="center" gap={16} className={styles.profileSection}>
          <Image src={image} width={80} height={80} className={styles.profileImage} preview={false} />

          <Flex vertical gap={4} className={styles.profileInfo}>
            <Flex align="center" gap={24} className={styles.nameRow}>
              <h1 className={styles.name}>{name}</h1>
              <Flex gap={8} className={styles.badges}>
                <Tag className={styles.roleTag}>{role}</Tag>
                <Tag className={styles.statusTag}>{status}</Tag>
              </Flex>
            </Flex>
            <p className={styles.title}>{title}</p>
            <p className={styles.description}>{description}</p>
          </Flex>

          <Flex gap={12} align="center" className={styles.actions}>
            <Button type="primary" onClick={handleEdit} className={styles.editButton}>
              {t("crewMember.detail.buttons.edit")}
            </Button>
            <Dropdown
              trigger={["click"]}
              menu={{
                items: [
                  { key: "deactivate", label: t("crewMember.detail.actions.deactivate") },
                  { key: "delete", label: t("crewMember.detail.actions.delete"), danger: true }
                ],
                onClick: ({ key }) => {
                  if (key === "deactivate") {
                    openDeactivateModal({ id, name, role })
                  } else if (key === "delete") {
                    openDeleteModal({ id, name, role })
                  }
                }
              }}
              placement="bottomRight"
            >
              <MoreActionEllipsis ellipsisDirection="vertical" />
            </Dropdown>
          </Flex>
        </Flex>
      </div>

      {/* Tabs */}
      <div className={styles.tabsContainer}>
        <DSTab items={tabItems} activeKey={activeTab} onChange={handleTabChange} />
      </div>

      {/* Content */}
      {activeTab === "details" && (
        <Flex vertical gap={16} className={styles.content}>
          {/* Upcoming Bookings Section */}
          <UpcomingBookings bookings={upcomingBookings} totalCount={upcomingBookings?.length} />

          {/* Availability and Time Off Row */}
          <div className={styles.twoColumnRow}>
            <Availability availability={availability} />
            <ScheduledTimeOff timeOffData={scheduledTimeOff} />
          </div>

          {/* Assigned Experiences and Licenses Row */}
          <div className={styles.twoColumnRow}>
            <AssignedExperiences
              experiences={assignedExperiences}
              onAssign={() => setIsAssignExperiencesModalOpen(true)}
            />
            <LicensesCertifications licenses={licenses} />
          </div>
        </Flex>
      )}

      {activeTab === "calendar" && (
        <Flex vertical gap={16} className={styles.content}>
          <Calendar crewMemberId={id} />
        </Flex>
      )}

      {/* Edit Crew Member Modal */}
      <EditCrewMemberModal
        open={isEditModalOpen}
        onCancel={() => setIsEditModalOpen(false)}
        onSave={handleSaveEdit}
        crewMember={crewMember}
        roles={roles}
      />

      {/* Assign Experiences Modal */}
      <AssignExperiencesModal
        open={isAssignExperiencesModalOpen}
        onCancel={() => setIsAssignExperiencesModalOpen(false)}
        onSave={async (selectedExperienceIds) => {
          const formData = new FormData()
          for (const tripId of selectedExperienceIds) {
            formData.append("profile[trip_ids][]", tripId)
          }
          if (selectedExperienceIds.length === 0) {
            formData.append("profile[trip_ids][]", "")
          }
          await dispatch(updateCrewProfile(id, formData))
          await dispatch(getCrewProfile(id))
        }}
        currentExperiences={assignedExperiences}
        emptyText={t("crewMember.no_experience_assigned", { defaultValue: "No experience assigned to this crew member." })}
      />

      {/* Deactivate/Delete Modal */}
      <DeactivateDeleteModals
        modalState={deactivateDeleteModalState}
        onClose={closeDeactivateDeleteModal}
        onConfirm={async (data) => {
          const result = await handleConfirmDeactivateDelete(data)
          if (result.success) {
            toaster.success(
              result.action === "deactivate"
                ? t("crewMember.detail.messages.deactivateSuccess")
                : t("crewMember.detail.messages.deleteSuccess")
            )
            if (result.action === "delete") {
              navigate(routes.guideCrewMembersPath())
            } else {
              dispatch(getCrewProfile(id))
            }
          }
        }}
        deactivateLoading={deactivateLoading}
        deleteLoading={deleteLoading}
        deactivationDetailsLoading={deactivationDetailsLoading}
        deletionDetailsLoading={deletionDetailsLoading}
        t={t}
      />
    </Flex>
  )
}
